/**
 * What a guest's submitted order may say about money.
 *
 * Port of iOS `OrderPaymentDisplay.swift`, which in turn mirrors the Bar app's
 * `paymentDisplayState`. Three platforms answering this question differently is how a
 * guest is told "Paid" on one screen and nothing on another for the same order, so the
 * rule is kept identical and tested on each.
 */

export type OrderPaymentState = 'paid' | 'refunded' | 'nothingToSay';

/**
 * ⚠️ Refunded is checked FIRST, before paid. If a backend ever sends both — and a
 * refunded order legitimately still carries `set_paid: true`, because it WAS paid —
 * "the money went back" is the fact the guest must not miss.
 *
 * ⚠️ Paid is `setPaid === true` and nothing else. Never derive it from `date_paid`:
 * Woo stamps that field on cash orders too, because Feather creates every order at
 * `processing`, so reading it would tell a guest who has not paid anything yet that
 * their order is settled.
 */
export function orderPaymentState(
  status?: string | null,
  setPaid?: boolean | null,
  refundedAt?: string | null,
): OrderPaymentState {
  const refundedAtIsPresent = (refundedAt ?? '').trim() !== '';

  if (status?.toLowerCase() === 'refunded' || refundedAtIsPresent) {
    return 'refunded';
  }

  if (setPaid === true) {
    return 'paid';
  }

  return 'nothingToSay';
}

/**
 * The i18n key for the line shown on the order, or null when this order has nothing to
 * say about money — which is the ordinary case for cash and pay-at-the-table orders.
 *
 * ⚠️ These live under `orderDetails.orderDrawer.`, NOT under `orderDetails.payment.`.
 * `payment.refunded` is already the outstanding-payment panel's full sentence ("This
 * payment was refunded. Your card was charged and the amount has been returned."), and
 * dropping a paragraph into a one-line status row would look like a bug rather than an
 * answer.
 */
export function orderPaymentSummaryKey(state: OrderPaymentState): string | null {
  switch (state) {
    case 'paid':
      return 'orderDetails.orderDrawer.paidOnline';
    case 'refunded':
      return 'orderDetails.orderDrawer.refunded';
    default:
      return null;
  }
}
